import { PackagingUnitDto } from 'src/app/data-transfer/entities/packaging-unit-entities/packaging-unit-dto';
import { ChangeMaterialCandidateDto } from '../../../../../data-transfer/entities/material-entities/change-material-candidate-dto';
import { ImportMaterialCandidateDto } from '../../../../../data-transfer/entities/material-entities/import-material-candidate-dto';
import { MatSort, Sort } from '@angular/material/sort';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { ImportComponentCandidateDto } from '../../../../../data-transfer/entities/component-entities/import-component-candidate-dto';
import { ChangeComponentCandidateDto } from '../../../../../data-transfer/entities/component-entities/change-component-candidate-dto';
import { ChangePackagingUnitCandidateDto } from '../../../../../data-transfer/entities/packaging-unit-entities/change-packaging-unit-candidate-dto';
import { ImportPackagingUnitCandidateDto } from '../../../../../data-transfer/entities/packaging-unit-entities/import-packaging-unit-candidate-dto';
import { ChangeCandidateDto } from './../../../../../data-transfer/entities/change-candidate-dto';
import { ImportCandidateDto } from './../../../../../data-transfer/entities/import-candidate-dto';
import { Component, Input, Output, EventEmitter, ViewChild, OnDestroy, signal, computed } from '@angular/core';
import { PackagingComponentDto } from 'src/app/data-transfer/entities/component-entities/packaging-component-dto';
import { MultiMaterialCompositeDto } from 'src/app/data-transfer/entities/material-entities/multi-material-composite-dto';
import { MatTableDataSource } from '@angular/material/table';
import { ImportPackagingSystemCandidateDto } from 'src/app/data-transfer/entities/packaging-system-entities/import-packaging-system-candidate-dto';
import { ChangePackagingSystemCandidateDto } from 'src/app/data-transfer/entities/packaging-system-entities/change-packaging-system-candidate-dto';
import { PackagingSystemDto } from 'src/app/data-transfer/entities/packaging-system-entities/packaging-system-dto';
import { PackagingPart } from 'src/app/model/packaging-part-enum';
import { PaginationDataSource } from 'src/app/util/pagination-datasource';
import { ImportCandidatePaginationFields } from 'src/app/data-transfer/entities/pagination-fields';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-import-html',
  templateUrl: './import-html.component.html',
  styleUrls: ['./import-html.component.scss']
})
export class ImportHtmlComponent implements OnDestroy {

  @Input() displayedColumns: string[] = [];
  // TODO: when Change by-page is available, switch selectedCandidates to an ID-based selection similar to selectedCandidateIds for Import.
  @Input() selectedCandidates: ChangeCandidateDto[] = [];
  @Input() selectedCandidateIds: Set<number> = new Set<number>();
  @Input() pageSizeOptions: number[] = [10, 25, 50, 100];
  @Input({required: true}) packagingPart!: PackagingPart;
  @Input() isChange = false;

  @Output() candidatesSelected = new EventEmitter();
  @Output() candidateIdsSelected = new EventEmitter<Set<number>>();
  @Output() navigationClicked = new EventEmitter();
  @Output() pageSizeChanged = new EventEmitter<number>();

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  mappedDataSource!: MatTableDataSource<ImportCandidateDto | ChangeCandidateDto>;
  allCandidateIds: number[] = [];
  totalItemCount = 0;
  currentPageIndex = 0;


  private readonly isLoadingSignal = signal(false);
  readonly isLoading = this.isLoadingSignal.asReadonly();
  private readonly currentPageSizeSignal = signal(10);
  readonly currentPageSize = this.currentPageSizeSignal.asReadonly();
  readonly loadingPlaceholderRows = computed(() => Array.from({ length: this.currentPageSizeSignal() }, () => ({})));

  private paginationDataSource?: PaginationDataSource<ImportCandidateDto>;
  private paginationSubscription?: Subscription;

  protected readonly paginationFields = ImportCandidatePaginationFields;

  get isPackagingSystem(): boolean {
    return this.packagingPart === PackagingPart.System;
  }

  get isPackagingUnit(): boolean {
    return this.packagingPart === PackagingPart.Unit;
  }

  get isComponent(): boolean {
    return this.packagingPart === PackagingPart.Component;
  }

  get isMaterial(): boolean {
    return this.packagingPart === PackagingPart.Material;
  }

  get isAllCandidatesSelected(): boolean {
    return !!this.mappedDataSource && this.mappedDataSource.data.length > 0 && this.selectedCandidates.length === this.mappedDataSource.data.length;
  }

  get isAllCandidateIdsSelected(): boolean {
    return this.allCandidateIds.length > 0 && this.allCandidateIds.every(id => this.selectedCandidateIds.has(id));
  }

  sortingDataAccessor = (item: any, property: any) => {
    switch (property) {
      case 'exportingOrganization': return item.exportingOrganization.name;
      default: return item[property];
    }
  }

  ngOnDestroy(): void {
    this.paginationSubscription?.unsubscribe();
  }

  setDataSource(
    updatedDataSource: MatTableDataSource<ImportCandidateDto> | MatTableDataSource<ChangeCandidateDto> | PaginationDataSource<ImportCandidateDto>,
    pageSize?: number
  ) {
    this.paginationSubscription?.unsubscribe();

    // Resync the paginator's displayed page size with whatever size this fresh load was actually
    // fetched with (initial load, or a packaging-part/archive-view reload) - otherwise it keeps
    // showing its last value even though the caller may be fetching with a different size.
    if (pageSize !== undefined) {
      this.currentPageSizeSignal.set(pageSize);
    }

    if (updatedDataSource instanceof PaginationDataSource) {
      this.paginationDataSource = updatedDataSource;

      const pageSubscription = updatedDataSource.page.subscribe(page => {
        this.totalItemCount = page.totalItemCount;
        this.currentPageIndex = page.pageIndex;
        this.allCandidateIds = page.allIds;
        this.pruneSelectedCandidateIds(page.allIds);
        this.applyMappedData(page.items);
      });
      const loadingSubscription = updatedDataSource.isLoading.subscribe(loading => {
        this.isLoadingSignal.set(loading);
      });
      this.paginationSubscription = new Subscription();
      this.paginationSubscription.add(pageSubscription);
      this.paginationSubscription.add(loadingSubscription);
      return;
    }

    // TO-DO: only reached by the Changes tab now, since Import always passes a
    // PaginationDataSource - kept only because Changes has no by-page backend endpoint yet.
    // Once one exists: build a PaginationDataSource<ChangeCandidateDto> in
    // ImportPackagingComponent.loadChangeCandidates() (mirroring loadImportCandidates()) and pass
    // that in here instead of a MatTableDataSource, then delete this branch entirely - setDataSource
    // will always take the PaginationDataSource branch above.
    this.paginationDataSource = undefined;
    this.isLoadingSignal.set(false);
    this.totalItemCount = updatedDataSource.data.length;
    this.currentPageIndex = 0;
    this.applyMappedData(updatedDataSource.data);
    this.mappedDataSource.sort = this.sort;
    this.mappedDataSource.sortingDataAccessor = this.sortingDataAccessor;
    this.mappedDataSource.paginator = this.paginator;
    this.paginator?.firstPage();
  }

  onPageChanged(event: PageEvent) {
    this.currentPageSizeSignal.set(event.pageSize);
    this.pageSizeChanged.emit(event.pageSize);
    this.paginationDataSource?.fetchData(event.pageIndex, event.pageSize);
  }

  private pruneSelectedCandidateIds(allIds: number[]) {
    const prunedIds = new Set([...this.selectedCandidateIds].filter(id => allIds.includes(id)));
    if (prunedIds.size !== this.selectedCandidateIds.size) {
      this.selectedCandidateIds = prunedIds;
      this.candidateIdsSelected.emit(prunedIds);
    }
  }

  onSortChanged(event: Sort) {
    if (!this.paginationDataSource) {
      return;
    }

    const fieldName = this.getPaginationFieldName(event.active);
    if (!fieldName) {
      return;
    }

    if (event.direction) {
      this.paginationDataSource.sort(fieldName, event.direction === 'asc');
    } else {
      this.paginationDataSource.removeSorting();
    }
  }

  private applyMappedData(entries: (ImportCandidateDto | ChangeCandidateDto)[]) {
    this.mappedDataSource = new MatTableDataSource<ImportCandidateDto | ChangeCandidateDto>();
    for (const entry of entries) {
      let mappedEntry: ImportCandidateDto | ChangeCandidateDto;
      if (this.isChange) {
        mappedEntry = this.setDataSourceChange(entry as ChangeCandidateDto) as ChangeCandidateDto;
      } else {
        mappedEntry = this.setDataSourceImport(entry as ImportCandidateDto) as ImportCandidateDto;
      }
      if (!mappedEntry) { continue; }
      for (const columnName of this.displayedColumns) {
        if (columnName === 'itemId') {
          mappedEntry['itemId' as keyof typeof mappedEntry] = this.getField(entry, 'id');
        } else if (columnName == 'timestamp') {
          if (this.isChange) {
            mappedEntry['lastModificationTimestamp' as keyof typeof mappedEntry] = this.getField(entry, 'lastModificationTimestamp');
          } else {
            continue; //export Date is not part of the packaging data bit of ImportCanditateDto and will be fetched in function 'setDataSourceImport'
          }
        } else {
          mappedEntry[columnName as keyof typeof mappedEntry] = this.getField(entry, columnName);
        }
      }
      mappedEntry.exportingOrganization = entry.exportingOrganization;
      this.mappedDataSource.data.push(mappedEntry);
    }
  }

  private setDataSourceImport(entry: ImportCandidateDto) {
    let mappedEntry;
    if (this.packagingPart === PackagingPart.System) {
      mappedEntry = new ImportPackagingSystemCandidateDto();
      mappedEntry.packagingSystem = (entry as ImportPackagingSystemCandidateDto).packagingSystem;
    } else if (this.packagingPart === PackagingPart.Unit) {
      mappedEntry = new ImportPackagingUnitCandidateDto();
      mappedEntry.packagingUnit = (entry as ImportPackagingUnitCandidateDto).packagingUnit;
    } else if (this.packagingPart === PackagingPart.Component) {
      mappedEntry = new ImportComponentCandidateDto();
      mappedEntry.packagingComponent = (entry as ImportComponentCandidateDto).packagingComponent;
    } else if (this.packagingPart === PackagingPart.Material) {
      mappedEntry = new ImportMaterialCandidateDto();
      mappedEntry.multiMaterial = (entry as ImportMaterialCandidateDto).multiMaterial;
    }
    if (!mappedEntry) { return; }
    mappedEntry.id = entry.id;
    mappedEntry.exportTimestamp = entry.exportTimestamp
    mappedEntry.hasNotification = entry.hasNotification;
    return mappedEntry;
  }

  private setDataSourceChange(entry: ChangeCandidateDto) {
    let mappedEntry;
    if (this.packagingPart === PackagingPart.System) {
      mappedEntry = new ChangePackagingSystemCandidateDto();
      mappedEntry.existingPackagingSystem = (entry as ChangePackagingSystemCandidateDto).existingPackagingSystem;
      mappedEntry.updatedPackagingSystem = (entry as ChangePackagingSystemCandidateDto).updatedPackagingSystem;
    } else if (this.packagingPart === PackagingPart.Unit) {
      mappedEntry = new ChangePackagingUnitCandidateDto();
      mappedEntry.existingPackagingUnit = (entry as ChangePackagingUnitCandidateDto).existingPackagingUnit;
      mappedEntry.updatedPackagingUnit = (entry as ChangePackagingUnitCandidateDto).updatedPackagingUnit;
    } else if (this.packagingPart === PackagingPart.Component) {
      mappedEntry = new ChangeComponentCandidateDto();
      mappedEntry.existingPackagingComponent = (entry as ChangeComponentCandidateDto).existingPackagingComponent;
      mappedEntry.updatedPackagingComponent = (entry as ChangeComponentCandidateDto).updatedPackagingComponent;
    } else if (this.packagingPart === PackagingPart.Material) {
      mappedEntry = new ChangeMaterialCandidateDto();
      mappedEntry.existingMultiMaterial = (entry as ChangeMaterialCandidateDto).existingMultiMaterial;
      mappedEntry.updatedMultiMaterial = (entry as ChangeMaterialCandidateDto).updatedMultiMaterial;
    }
    if (!mappedEntry) { return; }
    mappedEntry.importCandidateId = entry.importCandidateId;
    mappedEntry.hasNotification = entry.hasNotification;
    return mappedEntry;
  }

  navigateToCandidate(candidate: any, navigateToCurrentVersion?: boolean) {
    this.navigationClicked.emit({ candidate, navigateToCurrentVersion });
  }

  selectAll() {
    if (this.isChange) {
      if (!this.mappedDataSource) {
        return;
      }
      const selectAll = !this.isAllCandidatesSelected;
      this.selectedCandidates = selectAll ? [...this.mappedDataSource.data] as ChangeCandidateDto[] : [];
      this.candidatesSelected.emit(this.selectedCandidates);
      return;
    }

    const selectAll = !this.isAllCandidateIdsSelected;
    this.selectedCandidateIds = new Set<number>(selectAll ? this.allCandidateIds : []);
    this.candidateIdsSelected.emit(this.selectedCandidateIds);
  }

  isSelectedCandidate(candidate: any) {
    if (this.isChange) {
      return this.selectedCandidates.length > 0 && this.selectedCandidates.includes(candidate);
    }
    return this.selectedCandidateIds.has(candidate.id);
  }

  setSelectedCandidates(checked: boolean, candidate: any) {
    if (!this.isChange) {
      if (checked) {
        this.selectedCandidateIds.add(candidate.id);
      } else {
        this.selectedCandidateIds.delete(candidate.id);
      }
      this.candidateIdsSelected.emit(this.selectedCandidateIds);
      return;
    }

    if (checked) {
      this.selectedCandidates.push(candidate);
    } else {
      const index = this.selectedCandidates.indexOf(candidate);
      this.selectedCandidates.splice(index, 1);
    }
    this.candidatesSelected.emit(this.selectedCandidates);
  }

  private getField(candidate: any, fieldName: string): any {
    if (this.packagingPart === PackagingPart.System) {
      return this.isChange ?
        (candidate as ChangePackagingSystemCandidateDto).updatedPackagingSystem[fieldName as keyof PackagingSystemDto] :
        (candidate as ImportPackagingSystemCandidateDto).packagingSystem[fieldName as keyof PackagingSystemDto];
    } else if (this.packagingPart === PackagingPart.Unit) {
      return this.isChange ?
        (candidate as ChangePackagingUnitCandidateDto).updatedPackagingUnit[fieldName as keyof PackagingUnitDto] :
        (candidate as ImportPackagingUnitCandidateDto).packagingUnit[fieldName as keyof PackagingUnitDto];
    } else if (this.packagingPart === PackagingPart.Component) {
      return this.isChange ?
        (candidate as ChangeComponentCandidateDto).updatedPackagingComponent[fieldName as keyof PackagingComponentDto] :
        (candidate as ImportComponentCandidateDto).packagingComponent[fieldName as keyof PackagingComponentDto];
    } else if (this.packagingPart === PackagingPart.Material) {
      return this.isChange ?
        (candidate as ChangeMaterialCandidateDto).updatedMultiMaterial[fieldName as keyof MultiMaterialCompositeDto] :
        (candidate as ImportMaterialCandidateDto).multiMaterial[fieldName as keyof MultiMaterialCompositeDto];
    }
  }

  private getPaginationFieldName(column: string): string | undefined {
    switch (column) {
      case 'itemId': return this.paginationFields.Id;
      case 'version': return this.paginationFields.Version;
      case 'exportingOrganization': return this.paginationFields.OrganizationId;
      case 'productName': return this.paginationFields.ProductName;
      case 'brandName': return this.paginationFields.BrandName;
      case 'articleName': return this.paginationFields.ArticleName;
      case 'articleNumber': return this.paginationFields.ArticleNumber;
      case 'timestamp': return this.paginationFields.ExportTimestamp;
      case 'packagingTypeName': return this.paginationFields.PackagingType;
      case 'packagingComponentCategoryName': return this.paginationFields.ComponentCategory;
      case 'packagingComponentSubtypeName': return this.paginationFields.ComponentSubtype;
      default: return undefined;
    }
  }
}
